// The pay timeline: every pay an employment produces, with its gross,
// additional payments (bonus / one-off / grant tranche), PAYG withholding,
// salary sacrifice and employer super — then per-FY summaries combining all
// employments with the annual tax + super-cap calculators. Pure.
//
// Rules (agreed in the finance plan, P1c):
//  - A pay covers the days after the previous payday up to its own payday.
//    Its base gross is pro-rated BY DAY: each employed day contributes
//    (annual base in effect that day) ÷ (pays per year × days in the period),
//    so a mid-period salary change, a first partial pay (start date) and a
//    final partial pay (end date) all pro-rate.
//  - The final pay is the first payday on or after the end date.
//  - Salary, super and sacrifice come from the employment's pay reviews,
//    carried forward (income.js `resolveReviews`).
//  - The bonus for each FY worked pays on the employment's payout day after
//    the FY ends (if still employed then); its projection pro-rates the
//    review targets by day across the FY. An entered actual replaces it.
//  - Bonuses, one-offs and grant tranches are paid on the first payday on or
//    after their date and withheld with Schedule 5 Method A on that pay.
//    Grant tranches dated after the employment ends are forfeited unless
//    `keepAfterEnd`. Payments that fall after the last pay are dropped.
//  - Amounts in another currency convert to AUD at `fxRate(day)` (AUD per USD
//    — the RBA rate on/before that day, see market.js): grant tranches (the
//    entered actual, else the tranche value) on the payday, entered bonus
//    actuals on the payout day, one-offs on their date. With no rate the
//    payment counts AU$0 and is flagged `fxMissing`.
//  - A one-off spread over n pays is split evenly across the first n regular
//    pays on/after its date and withheld as ordinary pay (Schedule 1).
//  - An additional payment in a pay with no ordinary earnings (e.g. a bonus
//    paid during unpaid leave) is withheld against the FY-to-date average
//    ordinary earnings per period (`additionalBaselineCents`), as payroll does.
//  - Unpaid leave days earn no base pay (hence no super on it) and no bonus;
//    a leave's vesting delay pushes later grant tranches back (grantTranches).
//  - Contributions count in the FY of their payday.

import { allocateMinor, roundMinor, toAudCents } from './money.js';
import { audRateFor } from './market.js';
import {
  addDays,
  addMonths,
  currentFy,
  daysBetween,
  fyEnd,
  fyLabel,
  fyOf,
  fyStart,
  isoDate,
  monthLength,
} from './fy.js';
import { nextPayday, paydaysBetween, payPeriod } from './pay-schedule.js';
import {
  PERIODS_PER_YEAR,
  additionalPaymentWithholding,
  paygWithholding,
} from './au/withholding.js';
import { employerContributions, salarySacrificeFor, contributionCaps } from './au/super.js';
import { annualTax, daysInFy } from './au/tax.js';
import { fyAmounts, leavePeriods, onLeave, packageOn, resolveReviews } from './income.js';

/**
 * Convert a money value to AUD cents at the market rate on `day` (`fx` = AUD
 * per USD). `{ cents, rate, missing }` — no rate for its currency → 0 cents
 * and `missing` (the page warns).
 */
function audOn(value, fx, day) {
  const rate = audRateFor(value.currency, { usdRate: value.currency === 'AUD' ? null : fx(day) });
  const cents = toAudCents(value, rate);
  return cents === null ? { cents: 0, rate: null, missing: true } : { cents, rate, missing: false };
}

/**
 * Grant tranches: [{ index, date, value, actual, delayedPeriods }] — `value`
 * is the tranche's share of the grant total and `actual` the amount actually
 * paid when entered (money values; null = not entered).
 * Unpaid leave with a vesting delay (see income.js `leavePeriods`) moves every
 * tranche vesting on/after the leave start later by `vestingDelayPeriods` ×
 * the interval — only for a grant that existed during the leave
 * (`grantedOn` ≤ the leave end). Successive leaves compound.
 */
export function grantTranches(grant, { leaves = [], grantedOn = grant.firstVest } = {}) {
  const { currency } = grant.total;
  const amounts = allocateMinor(grant.total.amount, grant.tranches);
  const dates = vestDates(grant, { leaves, grantedOn });
  return amounts.map((amount, index) => ({
    index,
    date: dates[index].date,
    value: { amount, currency },
    actual: grant.actualByTranche?.[index] ?? null,
    delayedPeriods: dates[index].delayedPeriods,
  }));
}

/**
 * The vest dates of any grant on a regular schedule (`firstVest`, `tranches`,
 * `intervalMonths`) — cash or shares — with the unpaid-leave delay applied:
 * [{ date, delayedPeriods }] (see `grantTranches`).
 */
export function vestDates(
  { firstVest, tranches, intervalMonths },
  { leaves = [], grantedOn = firstVest } = {},
) {
  const vest = (periods) => addMonths(firstVest, periods * intervalMonths);
  return Array.from({ length: tranches }, (_, index) => {
    let shift = 0;
    for (const l of leaves) {
      if (l.vestingDelayPeriods > 0 && grantedOn <= l.end && vest(index + shift) >= l.start) {
        shift += l.vestingDelayPeriods;
      }
    }
    return { date: vest(index + shift), delayedPeriods: shift };
  });
}

/**
 * The payout date of the bonus earned over `fy`: the first `payout` ('MM-DD')
 * day AFTER the FY ends (30 June). Feb 29 clamps in non-leap years.
 */
export function bonusPayoutDate(payout, fy) {
  const [mm, dd] = payout.split('-').map(Number);
  const year = mm >= 7 ? fy : fy + 1;
  const first = `${year}-${String(mm).padStart(2, '0')}-01`;
  return addDays(first, Math.min(dd, monthLength(year, mm)) - 1);
}

/**
 * The projected bonus earned over `fy` (cents): each employed day of the FY
 * contributes (target % × annual base, or the target amount) ÷ days in the FY,
 * so raises, target changes and part years all pro-rate. Unpaid leave days
 * earn nothing (the bonus follows the base actually paid).
 */
export function projectedBonusCents(employment, resolved, fy, leaves = []) {
  const start = employment.startDate > fyStart(fy) ? employment.startDate : fyStart(fy);
  const fyLast = fyEnd(fy);
  const end = employment.endDate && employment.endDate < fyLast ? employment.endDate : fyLast;
  let sum = 0;
  for (let day = start; day <= end; day = addDays(day, 1)) {
    if (onLeave(leaves, day)) continue;
    const pkg = packageOn(resolved, day);
    const t = pkg?.bonusTarget;
    if (!t) continue;
    sum += t.kind === 'percent' ? pkg.annualCents * t.value : t.value;
  }
  return roundMinor(sum / daysInFy(fy));
}

/**
 * "Normal earnings" for withholding an additional payment (Schedule 5) paid in
 * a pay with NO ordinary earnings (e.g. a bonus during unpaid leave): the
 * FY-to-date average ordinary earnings per period at this employer, counting
 * this period — which matches what payroll was seen to do. With nothing earned
 * yet this FY, the package's regular per-period salary (after sacrifice); with
 * no package in force (after leaving), 0.
 * @param {{ ordinaryCents: number, periods: number }} ytd - includes this pay.
 * @param {object|null} pkg - the package in force during the period.
 * @param {number} ppy - pay periods per year.
 */
function additionalBaselineCents(ytd, pkg, ppy) {
  if (ytd.ordinaryCents > 0) return roundMinor(ytd.ordinaryCents / ytd.periods);
  if (!pkg) return 0;
  const regular = roundMinor(pkg.annualCents / ppy);
  return regular - salarySacrificeFor(regular, pkg.sacrifice ?? null);
}

/**
 * The bonus payments for an employment: [{ fy, date, amountCents, estimated,
 * fxMissing? }]. A projected bonus needs the employee still employed on the
 * payout day; an entered actual always pays — in another currency it converts
 * at the rate on the payout day (`fxRate`, AUD per USD; none → AU$0 +
 * `fxMissing`). Zero amounts are omitted.
 */
export function bonusPayouts(employment, resolved, through, leaves = [], fxRate = () => null) {
  const scheme = employment.bonus;
  if (scheme.offered === false) return [];
  const lastWorked =
    employment.endDate && employment.endDate < through ? employment.endDate : through;
  const out = [];
  for (let fy = fyOf(employment.startDate); fy <= fyOf(lastWorked); fy++) {
    const date = bonusPayoutDate(scheme.payout, fy);
    const actual = scheme.actualByFy[String(fy)] ?? null;
    const known = actual !== null;
    if (!known && employment.endDate && date > employment.endDate) continue;
    if (known) {
      const { cents, missing } = audOn(actual, fxRate, date);
      if (actual.amount > 0) {
        out.push({
          fy,
          date,
          amountCents: cents,
          estimated: false,
          ...(missing ? { fxMissing: true } : {}),
        });
      }
      continue;
    }
    const amountCents = projectedBonusCents(employment, resolved, fy, leaves);
    if (amountCents > 0) out.push({ fy, date, amountCents, estimated: true });
  }
  return out;
}

/** The default horizon for ongoing employments: the end of next FY. */
export function defaultHorizon(now = new Date()) {
  return fyEnd(currentFy(now) + 1);
}

/**
 * Every pay for ONE employment (normalised, see income.js) given its comp
 * events (any order), the income settings and an FX provider.
 * @returns {Array<object>} pays in date order.
 */
export function employmentPays(
  employment,
  events,
  settings,
  { fxRate, through = defaultHorizon() } = {},
) {
  const fx = fxRate ?? (() => null);
  const schedule = employment.pay;
  const ppy = PERIODS_PER_YEAR[schedule.frequency];
  const resolved = resolveReviews(events);
  const leaves = leavePeriods(events);
  const lastDay = employment.endDate && employment.endDate < through ? employment.endDate : through;
  if (lastDay < employment.startDate) return [];
  const firstPay = nextPayday(schedule, employment.startDate);
  const finalPay =
    employment.endDate && employment.endDate <= through
      ? nextPayday(schedule, employment.endDate)
      : through;
  const paydays = paydaysBetween(schedule, firstPay, finalPay);
  if (!paydays.length) return [];
  const regularPaydays = [...paydays]; // before any extra-only paydays are added

  // Additional payments, each attached to the first payday on/after its date.
  // A kept grant tranche after the final pay gets its own (extra-only) payday.
  const extrasByDay = new Map();
  const attach = (date, extra, { pastFinal = false } = {}) => {
    let day = paydays.find((p) => p >= date);
    if (!day && pastFinal) {
      day = nextPayday(schedule, date);
      if (day > through) return;
      paydays.push(day);
      paydays.sort();
    }
    if (!day) return;
    if (!extrasByDay.has(day)) extrasByDay.set(day, []);
    extrasByDay.get(day).push(extra);
  };
  for (const b of bonusPayouts(employment, resolved, through, leaves, fx)) {
    attach(
      b.date,
      {
        kind: 'bonus',
        eventRef: null,
        bonusFy: b.fy,
        label: `Bonus for ${fyLabel(b.fy)}`,
        amountCents: b.amountCents,
        estimated: b.estimated,
        ...(b.fxMissing ? { fxMissing: true } : {}),
        superApplies: employment.bonus.superApplies,
      },
      // An entered actual paid after leaving still lands (on an extra-only pay).
      { pastFinal: Boolean(employment.endDate) && b.date > employment.endDate },
    );
  }
  for (const ev of events) {
    if (ev.kind === 'one-off') {
      const n = ev.payPeriods ?? 1;
      // Another currency converts at the rate on the payment's date.
      const conv = audOn(ev.value, fx, ev.date);
      const missing = conv.missing ? { fxMissing: true } : {};
      if (!(ev.value.amount > 0)) continue;
      if (n <= 1 || conv.missing) {
        attach(ev.date, {
          kind: 'one-off',
          eventRef: ev.itemId ?? null,
          label: ev.label ?? 'One-off payment',
          amountCents: conv.cents,
          estimated: false,
          ...missing,
          superApplies: ev.superApplies,
        });
      } else {
        // Spread evenly over the first n regular pays on/after the date, each
        // instalment withheld as ORDINARY pay. Instalments past the last pay
        // are dropped.
        const first = regularPaydays.findIndex((p) => p >= ev.date);
        allocateMinor(conv.cents, n).forEach((amountCents, k) => {
          const day = first < 0 ? undefined : regularPaydays[first + k];
          if (!day || amountCents <= 0) return;
          if (!extrasByDay.has(day)) extrasByDay.set(day, []);
          extrasByDay.get(day).push({
            kind: 'one-off',
            eventRef: ev.itemId ?? null,
            label: `${ev.label ?? 'One-off payment'} — instalment ${k + 1} of ${n}`,
            instalment: k,
            ordinary: true,
            amountCents,
            estimated: false,
            superApplies: ev.superApplies,
          });
        });
      }
    } else if (ev.kind === 'review' && ev.grant) {
      const grant = ev.grant;
      for (const t of grantTranches(grant, { leaves, grantedOn: ev.date })) {
        const afterEnd = Boolean(employment.endDate) && t.date > employment.endDate;
        if (afterEnd && !grant.keepAfterEnd) continue;
        const day =
          paydays.find((p) => p >= t.date) ?? (afterEnd ? nextPayday(schedule, t.date) : null);
        if (!day || day > through) continue;
        // The actual paid (when entered) else the tranche value, converted at
        // the rate on the payday. No rate: keep the tranche visible but count
        // nothing in AUD — the page warns that rates are unavailable.
        const conv = audOn(t.actual ?? t.value, fx, day);
        attach(
          t.date,
          {
            kind: 'grant',
            eventRef: ev.itemId ?? null,
            label: `${ev.label ?? 'Grant'} — tranche ${t.index + 1} of ${grant.tranches}`,
            trancheIndex: t.index,
            value: t.value,
            fxRate: conv.missing || (t.actual ?? t.value).currency === 'AUD' ? null : conv.rate,
            amountCents: conv.cents,
            estimated: t.actual === null,
            ...(conv.missing ? { fxMissing: true } : {}),
            superApplies: grant.superApplies,
          },
          { pastFinal: afterEnd },
        );
      }
    }
  }

  const profile = { ...settings.profile, taxFreeThreshold: employment.taxFreeThreshold };
  // FY-to-date ordinary earnings + periods at this employer (this pay included),
  // the baseline for an additional payment in a pay with no ordinary earnings.
  const ytd = { fy: null, ordinaryCents: 0, periods: 0 };
  const pays = paydays.map((date) => {
    const period = payPeriod(schedule, date);
    const periodDays = daysBetween(period.start, period.end) + 1;
    let annualDaySum = 0;
    let base = null;
    for (let day = period.start; day <= period.end; day = addDays(day, 1)) {
      if (day < employment.startDate || (employment.endDate && day > employment.endDate)) continue;
      const pkg = packageOn(resolved, day);
      if (pkg) {
        // Unpaid leave days earn no base (the package still applies for super rate etc.).
        if (!onLeave(leaves, day)) annualDaySum += pkg.annualCents;
        base = pkg;
      }
    }
    const baseGrossCents = roundMinor(annualDaySum / (ppy * periodDays));
    const sacrificeCents = salarySacrificeFor(baseGrossCents, base?.sacrifice ?? null);
    const normalGross = baseGrossCents - sacrificeCents;
    const extras = extrasByDay.get(date) ?? [];
    const extrasCents = extras.reduce((s, x) => s + x.amountCents, 0);
    // Spread instalments are ordinary pay (Schedule 1 on the combined amount);
    // the rest are additional payments (Schedule 5 Method A).
    const ordinaryCents = extras.filter((x) => x.ordinary).reduce((s, x) => s + x.amountCents, 0);
    const additionalExtrasCents = extrasCents - ordinaryCents;
    const ordinaryGross = normalGross + ordinaryCents;
    if (ytd.fy !== fyOf(date)) Object.assign(ytd, { fy: fyOf(date), ordinaryCents: 0, periods: 0 });
    ytd.ordinaryCents += ordinaryGross;
    ytd.periods += 1;
    const opts = { frequency: schedule.frequency, date, profile };
    let taxCents;
    let stslCents;
    let additionalCents = 0;
    if (additionalExtrasCents > 0) {
      const r = additionalPaymentWithholding(
        ordinaryGross || additionalBaselineCents(ytd, base, ppy),
        additionalExtrasCents,
        opts,
      );
      // With a substituted baseline the pay's own (zero) earnings still
      // withhold nothing — only the additional payment's share is taken.
      ({ taxCents, stslCents } = ordinaryGross ? r.normal : paygWithholding(0, opts));
      additionalCents = r.additionalCents;
    } else {
      ({ taxCents, stslCents } = paygWithholding(ordinaryGross, opts));
    }
    const withheldCents = taxCents + stslCents + additionalCents;
    const grossCents = normalGross + extrasCents;
    return {
      employmentRef: employment.itemId ?? null,
      employer: employment.employer,
      scenario: employment.scenario,
      date,
      fy: fyOf(date),
      periodStart: period.start,
      periodEnd: period.end,
      frequency: schedule.frequency,
      baseGrossCents,
      sacrificeCents,
      extras,
      grossCents,
      taxCents,
      stslCents,
      additionalWithholdingCents: additionalCents,
      withheldCents,
      netCents: grossCents - withheldCents,
      superRate: base?.superRate ?? null,
      // Filled in below (MCB caps accumulate across this employer's pays).
      employerSuperCents: 0,
      mcbLimited: false,
    };
  });

  const contributions = employerContributions(
    pays.map((p) => ({
      date: p.date,
      oteCents:
        p.baseGrossCents -
        p.sacrificeCents +
        p.extras.filter((x) => x.superApplies).reduce((s, x) => s + x.amountCents, 0),
      sacrificeCents: p.sacrificeCents,
      rate: p.superRate ?? undefined,
    })),
  );
  pays.forEach((p, i) => {
    p.employerSuperCents = contributions[i].employerCents;
    p.superRate = contributions[i].rate;
    p.mcbLimited = contributions[i].mcbLimited;
  });
  return pays;
}

/**
 * Pays for ALL employments, date order. Scenario employments are excluded
 * unless `includeScenarios`.
 */
export function allPays(employments, events, settings, { includeScenarios = false, ...opts } = {}) {
  const pays = [];
  for (const emp of employments) {
    if (emp.scenario && !includeScenarios) continue;
    const own = events.filter((e) => e.employmentRef === emp.itemId);
    pays.push(...employmentPays(emp, own, settings, opts));
  }
  return pays.sort(
    (a, b) => a.date.localeCompare(b.date) || String(a.employer).localeCompare(String(b.employer)),
  );
}

/** Days in an FY not covered by any private hospital cover period. */
export function daysWithoutCover(coverPeriods, fy) {
  const start = fyStart(fy);
  const end = fyEnd(fy);
  let covered = 0;
  let cursor = start; // count each day once even if periods overlap
  for (const p of coverPeriods) {
    const from = p.from > cursor ? p.from : cursor;
    const to = p.to && p.to < end ? p.to : end;
    if (to < from) continue;
    covered += daysBetween(from, to) + 1;
    cursor = addDays(to, 1);
  }
  return daysInFy(fy) - covered;
}

function sumBy(pays, key) {
  return pays.reduce((s, p) => s + p[key], 0);
}

/**
 * Per-FY concessional/non-concessional records for the cap calculator, from
 * the first FY with any pay or per-FY figures through `throughFy`.
 */
export function capRecords(pays, settings, throughFy) {
  const fys = new Set(pays.map((p) => p.fy));
  for (const k of Object.keys(settings.byFy)) fys.add(Number(k));
  const first = Math.min(...fys, throughFy);
  const records = [];
  for (let fy = first; fy <= throughFy; fy++) {
    const inFy = pays.filter((p) => p.fy === fy);
    const fig = fyAmounts(settings, fy);
    const derived =
      sumBy(inFy, 'employerSuperCents') +
      sumBy(inFy, 'sacrificeCents') +
      fig.personalDeductibleCents;
    records.push({
      fy,
      concessionalCents: fig.concessionalOverrideCents ?? derived,
      nonConcessionalCents: fig.nonConcessionalCents,
      tsbPriorCents: fyAmounts(settings, fy - 1).tsbEndCents,
    });
  }
  return records;
}

/**
 * The FY summary: employment totals, super + caps, and the annual tax estimate
 * (employment income plus, when given, employee share scheme income — other
 * investment income and deductions arrive in later phases, noted in `notes`).
 * @param {{ incomeCents: number, withheldCents: number, estimated?: boolean,
 *   missing?: boolean }} [opts.shareIncome] - this FY's share-scheme (ESS)
 *   income + sell-to-cover withholding (shares.js `essByFy`).
 */
export function summariseFy(pays, settings, fy, { today = new Date(), shareIncome = null } = {}) {
  const inFy = pays.filter((p) => p.fy === fy);
  const fig = fyAmounts(settings, fy);
  const caps = contributionCaps(capRecords(pays, settings, fy)).find((r) => r.fy === fy);
  const excess = caps.concessional.excessCents;
  const sacrificeCents = sumBy(inFy, 'sacrificeCents');
  const grossCents = sumBy(inFy, 'grossCents');
  const ess = {
    incomeCents: shareIncome?.incomeCents ?? 0,
    withheldCents: shareIncome?.withheldCents ?? 0,
  };
  const withheldCents = sumBy(inFy, 'withheldCents') + ess.withheldCents;
  const tax = annualTax({
    fy,
    taxableIncomeCents: Math.max(0, grossCents + ess.incomeCents - fig.personalDeductibleCents),
    reportableSuperCents: sacrificeCents + fig.personalDeductibleCents,
    lowTaxContributionsCents: Math.max(0, caps.concessional.contributedCents - excess),
    daysWithoutCover: daysWithoutCover(settings.hospitalCover, fy),
    family: fig,
    studyLoan: settings.profile.studyLoan,
    withheldCents,
    excessConcessionalCents: excess,
  });
  return {
    fy,
    pays: inFy.length,
    grossCents,
    sacrificeCents,
    withheldCents,
    netCents: sumBy(inFy, 'netCents'),
    employerSuperCents: sumBy(inFy, 'employerSuperCents'),
    shareIncomeCents: ess.incomeCents,
    shareWithheldCents: ess.withheldCents,
    estimated:
      inFy.some((p) => p.extras.some((x) => x.estimated)) ||
      fy >= currentFy(today) ||
      shareIncome?.estimated === true,
    caps,
    tax,
    notes: [
      ...tax.notes,
      shareIncome
        ? 'Estimate covers employment and employee share scheme income only — other investment income and deductions come later.'
        : 'Estimate covers employment income only — investment income and deductions come later.',
      ...(shareIncome?.missing
        ? ['Some share vests have no price or exchange rate yet, so they count as AU$0.']
        : []),
      ...(settings.profile.foreignResident
        ? ['Annual estimate uses resident rates (foreign-resident rates not modelled).']
        : []),
      ...(settings.profile.medicareExemption !== 'none'
        ? ['Annual estimate charges the full Medicare levy (exemptions not modelled).']
        : []),
    ],
  };
}

/** The first pay on or after `today` (actual employments), or null. */
export function nextPay(pays, today = isoDate(new Date())) {
  return pays.find((p) => !p.scenario && p.date >= today) ?? null;
}
